import React, {useEffect, useRef, useState, Children, cloneElement} from 'react';
import useCurrentOrg from "../helpers/useCurrentOrg";
import {Button, ColorPicker, Divider, Flex, List, message, Modal, Popconfirm, Popover, Space, Table, Tag, Typography, Row, Col, Input, Empty, ConfigProvider} from "antd";
import {FieldArray, Formik, useFormikContext} from "formik";
import api from "../api";
import { handleApiError } from "../helpers/formatApiError";
import * as Yup from "yup";
import {
    CheckOutlined,
    EditOutlined,
    PlusOutlined,
    RollbackOutlined,
    SaveOutlined,
    SearchOutlined,
    MenuOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ImportOutlined,
    UploadOutlined,
    SelectOutlined
} from "@ant-design/icons";
import {Form, FormItem, Input as FormInput, Radio, Checkbox, Select, Switch, SubmitButton} from "formik-antd";
import FloatLabel from "../helpers/FloatLabel";
import {DeleteIcon} from "../helpers/icons";
import HelpPopover from "../HelpPopover";
import setTitle from "../helpers/setTitle";
import {useTranslation} from "react-i18next";
import _ from 'lodash';
import VerticalSpace from '../helpers/VerticalSpace';
import useCurrentUser from '../helpers/useCurrentUser';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactMarkdown from 'react-markdown';
import NoDragTextArea from '../widgets/NoDragTextArea';

export default ({ownerType='Asset', typeTitle='Meta'})=>{
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg()

    setTitle(t(`custom-${ownerType.toLowerCase()}-fields`,`Custom ${typeTitle} Fields`));

    const [loading, setLoading] = useState(false)
    const [customMetaFields, setCustomMetaFields] = useState([])
    const [searchValue, setSearchValue] = useState('')
    const [filters, setFilters] = useState({})

    const fetchData = (filters = {}) => {
        setLoading(true)
        
        const params = {
            q: searchValue,
            ...filters
        }
        
        api.get(`/api/custom_meta_fields?owner_type=${ownerType}`, { params }).then(res => {
            setLoading(false)
            setCustomMetaFields(res.data)
        })
    }

    useEffect(()=>{
        fetchData(filters)
    }, [currentOrg?.id, searchValue])

    const [newModalVisible, setNewModalVisible] = useState(false)
    const [importModalVisible, setImportModalVisible] = useState(false)
    const [selectedRowKeys, setSelectedRowKeys] = useState([])

    const clickNew = ()=>{
        setNewModalVisible(true)
        setTimeout(()=>{
            if (autoFocusInput.current) {
                autoFocusInput.current.focus()
            }
        }, 100)
    }

    const autoFocusInput = useRef(null);

    const exportSelected = () => {
        if (selectedRowKeys.length === 0) return;

        api.post('/api/custom_meta_fields/export', { ids: selectedRowKeys }).then(res => {
            const dataStr = JSON.stringify(res.data, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = selectedRowKeys.length === 1
                ? `${customMetaFields.find(c => c.id === selectedRowKeys[0])?.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_custom_field.json`
                : `custom_fields_export_${selectedRowKeys.length}.json`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success(t('message-exported-custom-fields', 'Exported {{count}} custom field(s).', { count: selectedRowKeys.length }));
            setSelectedRowKeys([]);
        }).catch(err => {
            message.error(t('message-export-failed', 'Failed to export custom field(s).'));
            console.error(err);
        });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
    };

    const updated = cmf => {
        const i = customMetaFields.map(cmf => cmf.id).indexOf(cmf.id)
        customMetaFields.splice(i, 1, cmf)
        setCustomMetaFields([...customMetaFields])
    }

    const destroy = cmf => {
        api.delete(`/api/custom_meta_fields/${cmf.id}`).then(res => {
            message.success(t(`message-custom-${ownerType.toLowerCase()}-field-removed`,`Custom ${typeTitle} Field removed.`))

            const i = customMetaFields.map(cmf => cmf.id).indexOf(cmf.id)
            customMetaFields.splice(i, 1)
            setCustomMetaFields([...customMetaFields])
        })
    }
    
    const handleSearch = _.debounce((value) => {
        setSearchValue(value)
    }, 300)
    
    // Custom row component for drag-and-drop that exactly follows the Ant Design example
    const Row = props => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
            id: props['data-row-key'],
        });
        
        const style = {
            ...props.style,
            transform: CSS.Translate.toString(transform),
            transition,
            ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        };
        
        return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
    };

    // No need for a separate DragHandle component since the entire row is draggable

    const columns = [
        {
            title: '',
            dataIndex: 'sort',
            width: 30,
            className: 'drag-visible',
            render: () => (
                <MenuOutlined
                    style={{
                        cursor: 'grab',
                        color: '#999',
                    }}
                />
            )
        },
        {
            title: t('enabled','Enabled').toProperCase(),
            dataIndex: 'enabled',
            filterMultiple: false,
            filters: [
                {text: t('enabled','Enabled').toProperCase(), value: 'true'},
                {text: t('disabled','Disabled').toProperCase(), value: 'false'},
            ],
            defaultFilteredValue: ['true'],
            render: (enabled)=> (
                <>{enabled && <CheckOutlined/>}</>
            )
        },
        {title: t('name','Name'), dataIndex: 'name'},
        {title: t('description','Description'), dataIndex: 'description'},
        {
            title: t('notes','Notes'),
            dataIndex: 'notes',
            width: 60,
            align: 'center',
            render: (notes) => (
                notes ? (
                    <Popover 
                        content={
                            <div style={{maxWidth: 400}}>
                                <ReactMarkdown>{notes}</ReactMarkdown>
                            </div>
                        } 
                        title={t('notes','Notes')}
                    >
                        <InfoCircleOutlined style={{cursor: 'pointer', color: '#1890ff'}} />
                    </Popover>
                ) : null
            )
        },
        {
            title: t('value','Value'),
            dataIndex: 'multi',
            filters: [
                {text: t('multi-value','Multi-Value'), value: 'true'},
                {text: t('single-value','Single-Value'), value: 'false'},
            ],
            render: (multi)=> (
                <>{multi ? t('multi-value','Multi-Value') : t('single-value','Single-Value')}</>
            )
        },
        {
            title: t('type','Type'),
            dataIndex: 'value_type',
            filters: [
                {text: t('text','Text'), value: 'text'},
                {text: t('date','Date'), value: 'date'},
                {text: t('datetime','Date and Time'), value: 'datetime'},
            ],
            render: (value_type)=> (
                <>{t(value_type, value_type?.toUpperCase ? value_type.toUpperCase() : value_type)}</>
            )
        },
        {
            title: t('controlled-vocab','Controlled Vocab'),
            dataIndex: 'free',
            filters: [
                {text: t('free-text','Free Text'), value: 'true'},
                {text: t('controlled','Controlled'), value: 'false'},
            ],
            render: (free, cmf)=> (
                <>{free ? t('free-text','Free Text') : <Tag>{cmf.custom_meta_values_attributes?.length || 0}</Tag>}</>
            )
        },
        {
            title: t('action-menu','Action Menu'),
            dataIndex: 'enable_action_menu',
            filters: [
                {text: t('enabled','Enabled'), value: 'true'},
                {text: t('disabled','Disabled'), value: 'false'},
            ],
            render: (enable_action_menu)=> (
                <>{enable_action_menu && <CheckOutlined/>}</>
            )
        },
    ]
    
    // Only add the AI Enabled column if the org has bedrock credentials
    if (currentOrg?.has_bedrock_credentials) {
        columns.push({
            title: t('ai-enabled','AI Enabled'),
            dataIndex: 'enable_ai',
            filters: [
                {text: t('enabled','Enabled'), value: 'true'},
                {text: t('disabled','Disabled'), value: 'false'},
            ],
            render: (enable_ai)=> (
                <>{enable_ai && <CheckOutlined/>}</>
            )
        })
    }

    // Role visibility column
    const roleLabels = {
        'general': t('general-members','General Members'),
        'global_tagger': t('global-taggers','Global Taggers'),
        'global_library': t('global-library-managers','Global Library Managers'),
        'global_content': t('global-content-managers','Global Content Managers'),
        'admin': t('admins','Admins'),
    }
    columns.push({
        title: t('visibility','Visibility'),
        dataIndex: 'minimum_role_level',
        filters: [
            {text: t('all-roles','All Roles'), value: 'null'},
            {text: t('general-members','General Members'), value: 'general'},
            {text: t('global-taggers','Global Taggers'), value: 'global_tagger'},
            {text: t('global-library-managers','Global Library Managers'), value: 'global_library'},
            {text: t('global-content-managers','Global Content Managers'), value: 'global_content'},
            {text: t('admins','Admins'), value: 'admin'},
        ],
        render: (minimum_role_level)=> {
            if (!minimum_role_level) {
                return <Tag color="green">{t('all-roles','All Roles')}</Tag>
            }
            const roleName = roleLabels[minimum_role_level] || minimum_role_level
            return <Tag>{roleName} {t('and-above','and above')}</Tag>
        }
    })

    columns.push({
        title: t('actions','Actions'),
        dataIndex: 'actions',
        width: 100,
        render: (_, cmf)=> (
            <Space style={{textAlign:'right', marginLeft:'auto'}}>
                <CustomMetaEditButton cmf={cmf} updated={updated} ownerType={ownerType} typeTitle={typeTitle}/>
                <Popconfirm title={t('confirm-delete-custom-meta-field','Delete Custom Meta Field?')} onConfirm={()=> destroy(cmf)}>
                    <Button><DeleteIcon/></Button>
                </Popconfirm>
            </Space>
        )
    })

    const handleTableChange = (pagination, filters) => {
        setFilters(filters)
        fetchData(filters)
    }
    
    // Configure the sensors for drag detection
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        }),
    );
    
    // Handle drag end event
    const onDragEnd = ({ active, over }) => {
        if (active.id !== over?.id) {
            const oldIndex = customMetaFields.findIndex(i => i.id === active.id);
            const newIndex = customMetaFields.findIndex(i => i.id === over?.id);
            
            if (oldIndex !== -1 && newIndex !== -1) {
                const newData = arrayMove(customMetaFields.slice(), oldIndex, newIndex);
                
                // Use the actual position values from the records, not array indexes
                const oldPosition = customMetaFields[oldIndex].position;
                const newPosition = customMetaFields[newIndex].position;
                
                api.put('/api/custom_meta_fields/update_position', {
                    oldIndex: oldPosition, 
                    newIndex: newPosition
                }).then(res => {
                    message.success(t('message-sort-order-updated','Sort order updated!'))
                }).catch(err => {
                    message.error('Failed to update sort order')
                    console.error(err)
                });
                
                setCustomMetaFields(newData);
            }
        }
    };

    return (
        <div style={{paddingTop:'1em'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '1em'}}>
                <Space>
                    <Button onClick={clickNew} type={'primary'} icon={<PlusOutlined/>}>{t(`button-add-custom-${ownerType.toLowerCase()}-field`,`Add Custom ${typeTitle} Field`)}</Button>
                    <Button onClick={() => setImportModalVisible(true)} icon={<ImportOutlined/>}>{t('import','Import')}</Button>
                </Space>

                <Input
                    placeholder={t('search-custom-fields','Search custom fields...')}
                    prefix={<SearchOutlined />}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{width: 250}}
                />
                
                <CustomMetaModal
                    modalVisible={newModalVisible}
                    setModalVisible={setNewModalVisible}
                    onSuccess={cmf => {
                        setCustomMetaFields([...customMetaFields, cmf])
                        fetchData(filters)
                    }}
                    autoFocusInput={autoFocusInput}
                    ownerType={ownerType}
                    typeTitle={typeTitle}
                />

                <ImportModal
                    visible={importModalVisible}
                    setVisible={setImportModalVisible}
                    onSuccess={cmf => {
                        setCustomMetaFields([...customMetaFields, cmf])
                        fetchData(filters)
                    }}
                    ownerType={ownerType}
                    typeTitle={typeTitle}
                />
            </div>

            <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={customMetaFields.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <ConfigProvider renderEmpty={() => <Empty description={t('no-custom-fields-yet','No custom fields yet.')} />}>
                        <Table
                            size={'small'}
                            pagination={false}
                            dataSource={customMetaFields}
                            columns={columns}
                            rowKey="id"
                            loading={loading}
                            rowSelection={rowSelection}
                            components={{
                                body: {
                                    row: Row
                                },
                            }}
                            onChange={handleTableChange}
                            bordered
                            footer={() => (
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                                    <Space>
                                        {selectedRowKeys.length > 0 && (
                                            <Button type="primary" icon={<ExportOutlined/>} onClick={exportSelected}>
                                                {t('export', 'Export')} {selectedRowKeys.length}
                                            </Button>
                                        )}
                                    </Space>
                                    <Typography.Text type="secondary">
                                        {t('total-count', 'Total: {{count}}', { count: customMetaFields.length })}
                                    </Typography.Text>
                                </div>
                            )}
                        />
                    </ConfigProvider>
                </SortableContext>
            </DndContext>
        </div>
    )
}

const AiModelSelectorModal = ({ value, onChange }) => {
    const {t} = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);

    const availableModels = window.Config?.aiModels || [];
    const currentModel = availableModels.find(m => m.id === value);

    // Format price for display
    const formatPrice = (price) => {
        if (price === 0 || price === undefined || price === null) return '-';
        if (price >= 0.001) {
            return `$${price.toFixed(4)}`;
        }
        return `$${price.toFixed(6)}`;
    };

    // Get effective price for sorting (use input price, or second price for duration-based)
    const getEffectivePrice = (model) => {
        if (model.isDurationBased) {
            return model.secondPrice || 0;
        }
        return model.inputPrice || 0;
    };

    // Get asset type tags for a model
    const getAssetTypeTags = (model) => {
        const tags = [];
        if (model.supportsImage) tags.push({ label: t('image', 'Image'), color: 'blue' });
        if (model.supportsVideo) tags.push({ label: t('video', 'Video'), color: 'purple' });
        if (model.supportsAudio) tags.push({ label: t('audio', 'Audio'), color: 'orange' });
        if (model.supportsDocument) tags.push({ label: t('document', 'Document'), color: 'cyan' });
        return tags;
    };

    const columns = [
        {
            title: t('model', 'Model'),
            key: 'name',
            width: 280,
            render: (_, model) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{model.name}</div>
                    {model.description && (
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            {model.description}
                        </Typography.Text>
                    )}
                </div>
            )
        },
        {
            title: t('types', 'Types'),
            key: 'assetTypes',
            width: 120,
            render: (_, model) => (
                <Space size={2} wrap>
                    {model.supportsImage && <Tag color="blue" style={{marginRight: 0}}>Img</Tag>}
                    {model.supportsVideo && <Tag color="purple" style={{marginRight: 0}}>Vid</Tag>}
                    {model.supportsAudio && <Tag color="orange" style={{marginRight: 0}}>Aud</Tag>}
                    {model.supportsDocument && <Tag color="cyan" style={{marginRight: 0}}>Doc</Tag>}
                </Space>
            )
        },
        {
            title: t('pricing', 'Pricing'),
            key: 'pricing',
            width: 160,
            sorter: (a, b) => getEffectivePrice(a) - getEffectivePrice(b),
            showSorterTooltip: { title: t('sort-by-price', 'Sort by price') },
            render: (_, model) => (
                <div style={{ fontSize: '12px' }}>
                    {model.isDurationBased ? (
                        <>
                            <div>{formatPrice(model.secondPrice)}/sec</div>
                            <div>{formatPrice(model.outputPrice)}/1K out</div>
                        </>
                    ) : (
                        <>
                            <div>{formatPrice(model.inputPrice)}/1K in</div>
                            <div>{formatPrice(model.outputPrice)}/1K out</div>
                        </>
                    )}
                </div>
            )
        },
        {
            title: '',
            key: 'action',
            width: 100,
            render: (_, model) => (
                model.id === value ? (
                    <Tag color="green">{t('current', 'Current')}</Tag>
                ) : (
                    <Button
                        size="small"
                        ghost
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => {
                            onChange(model.id);
                            setModalVisible(false);
                        }}
                    >
                        {t('select', 'Select')}
                    </Button>
                )
            )
        }
    ];

    // Deduplicate models by model_id (keep first occurrence)
    const uniqueModels = availableModels.filter((model, index, self) =>
        index === self.findIndex(m => m.id === model.id)
    );

    // Include deprecated model if currently selected but not in available models
    const modelExists = uniqueModels.some(m => m.id === value);
    const modelsToShow = !modelExists && value
        ? [{
            key: `deprecated-${value}`,
            id: value,
            name: `${value}`,
            description: t('deprecated-model', 'This model is deprecated'),
            supportsImage: false,
            supportsVideo: false,
            supportsAudio: false,
            supportsDocument: false,
            inputPrice: 0,
            outputPrice: 0,
            isDurationBased: false
          }, ...uniqueModels]
        : uniqueModels;

    return (
        <>
            <Button
                onClick={() => setModalVisible(true)}
                style={{ width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {currentModel?.name || value || t('select-model', 'Select a model...')}
                </span>
                <Space size={4} style={{ marginLeft: 8, flexShrink: 0 }}>
                    {currentModel && getAssetTypeTags(currentModel).map(tag => (
                        <Tag key={tag.label} color={tag.color} style={{ marginRight: 0 }}>{tag.label}</Tag>
                    ))}
                    <SelectOutlined />
                </Space>
            </Button>

            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                title={t('select-ai-model', 'Select AI Model')}
                footer={null}
                width={700}
            >
                <Table
                    dataSource={modelsToShow}
                    columns={columns}
                    rowKey="key"
                    pagination={false}
                    size="small"
                />
            </Modal>
        </>
    );
};

const CustomMetaEditButton = ({cmf, updated, ownerType, typeTitle})=>{
    const {t} = useTranslation();
    const [modalVisible, setModalVisible] = useState(false)

    return (
        <>
            <Button icon={<EditOutlined/>} onClick={()=> setModalVisible(true)}>{t('button-edit','Edit')}</Button>

            <CustomMetaModal cmf={cmf} modalVisible={modalVisible} setModalVisible={setModalVisible} onSuccess={updated} ownerType={ownerType} typeTitle={typeTitle} autoFocusInput={null} />
        </>
    )
}

const CustomMetaModal = ({cmf = {free: false, multi: false, value_type: 'text', asset_types: ['image', 'video', 'audio', 'document'], source_data: 'asset', model: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0', prompt: '', destination: 'none', auto_run: false, enable_controlled_vocab_creation: false, enabled: true, notes: ''}, modalVisible, setModalVisible, onSuccess, autoFocusInput, ownerType, typeTitle})=>{
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg()
    const currentUser = useCurrentUser()

    return (
        (<Formik
            initialValues={cmf}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
                const method = cmf.id ? api.put : api.post;
                values.owner_type = ownerType

                method(`/api/custom_meta_fields/${cmf.id || ''}`, {custom_meta_field: values}).then(res => {
                    actions.resetForm();
                    actions.setSubmitting(false);

                    if(cmf.id) {
                        message.success(t(`custom-${ownerType.toLowerCase()}-field-updated`,`Custom ${typeTitle} Field "{{name}}" updated.`, {name: values.name}))
                    } else {
                        message.success(t(`custom-${ownerType.toLowerCase()}-field-created`,`Custom ${typeTitle} Field "{{name}}" created.`, {name: values.name}))
                    }

                    onSuccess(res.data)
                    setModalVisible(false);
                }).catch((err)=>{
                    actions.setSubmitting(false);
                    handleApiError(err)
                })
            }}
            validationSchema={
                Yup.object({
                    name: Yup.string().required(''), // TODO: check uniqueness
                })
            }
        >
            {({values, submitForm, handleSubmit, dirty, setFieldValue, isSubmitting, resetForm}) => {
                return (
                    (<Modal
                        open={modalVisible}
                        onCancel={()=> setModalVisible(false)}
                        title={<>{cmf.id ? t('edit','Edit') : t('new','New')} Custom {typeTitle} Field</>}
                        zIndex={9999}
                        footer={
                            <Space direction={'horizontal'}>
                                <Button type={'primary'} onClick={submitForm} loading={isSubmitting}>
                                    <SaveOutlined/>
                                    {cmf.id ? t('update','Update') : t('create','Create')}
                                </Button>
                                <Popconfirm
                                    title={t('confirm-cancel-edit-and-reset','Cancel Edit and Reset?')}
                                    onConfirm={()=>{
                                        resetForm()
                                        setModalVisible(false);
                                    }}
                                >
                                    <Button>{t('button-cancel','Cancel')}</Button>
                                </Popconfirm>
                            </Space>
                        }
                    >
                        <Form>
                            <FormItem name='enabled'>
                                <Switch checkedChildren={t('enabled','Enabled').toProperCase()} unCheckedChildren={t('disabled','Disabled').toProperCase()} name='enabled'/>
                            </FormItem>

                            <FormItem required name='name' showValidateSuccess>
                                <FloatLabel label={t('name','Name')} name={'name'} value={values?.name} >
                                    <FormInput size={'large'} required name='name' ref={autoFocusInput} autoFocus autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <FormItem name='description'>
                                <FloatLabel label={t('description-optional','Description (optional)')} name={'description'} value={values?.description} >
                                    <NoDragTextArea size={'large'} name='description' autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <FormItem name='notes'>
                                <FloatLabel label={t('admin-notes-optional','Admin Notes (optional)')} name={'notes'} value={values?.notes} >
                                    <NoDragTextArea size={'large'} name='notes' autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <FormItem name='minimum_role_level' label={<>{t('minimum-role-visibility','Minimum Role Visibility')} <HelpPopover code={'custom-meta-field-visibility'}/></>}>
                                <Select name={'minimum_role_level'} allowClear placeholder={t('all-roles-default','All Roles (default)')}>
                                    <Select.Option value={'general'}>{t('general-members','General Members')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'global_tagger'}>{t('global-taggers','Global Taggers')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'global_library'}>{t('global-library-managers','Global Library Managers')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'global_content'}>{t('global-content-managers','Global Content Managers')} {t('and-above','and above')}</Select.Option>
                                    <Select.Option value={'admin'}>{t('admins','Admins')} {t('only','only')}</Select.Option>
                                </Select>
                            </FormItem>

                            {currentOrg?.has_bedrock_credentials && (
                                <>
                                    <Divider>{t('ai-settings', 'AI Settings')}</Divider>
                                    
                                    <FormItem name='enable_ai'>
                                        <Checkbox name={'enable_ai'}>{t('enable-ai','Enable AI for this field?')}</Checkbox>
                                    </FormItem>
                                    
                                    {values.enable_ai && (
                                        <>
                                            <FormItem name='model' label={<>{t('ai-model', 'AI Model')} <HelpPopover code={'custom-meta-ai-model'}/></>}>
                                                <AiModelSelectorModal
                                                    value={values.model}
                                                    onChange={(modelId) => setFieldValue('model', modelId)}
                                                />
                                            </FormItem>
                                            
                                            <FormItem name='prompt' label={<>{t('ai-prompt', 'AI Prompt')} <HelpPopover code={'custom-meta-ai-prompt'}/></>}>
                                                <NoDragTextArea 
                                                    rows={3} 
                                                    name={'prompt'} 
                                                    placeholder={t('ai-prompt-placeholder', 'Instructions for the AI model...')}
                                                />
                                            </FormItem>
                                            
                                            <FormItem name='source_data' label={<>{t('source-data', 'Source Data')} <HelpPopover code={'custom-meta-ai-source-data'}/></>}>
                                                <Select name={'source_data'}>
                                                    <Select.Option value={'asset'}>{t('asset-data', 'Asset Data')}</Select.Option>
                                                    <Select.Option value={'text'}>{t('asset-extracted-text', 'Extracted Text')}</Select.Option>
                                                </Select>
                                            </FormItem>
                                            
                                            <FormItem name='asset_types' label={<>{t('asset-types', 'Asset Types')} <HelpPopover code={'custom-meta-ai-asset-types'}/></>}>
                                                <Checkbox.Group name={'asset_types'}>
                                                    <Checkbox value={'image'}>{t('image', 'Images')}</Checkbox>
                                                    <Checkbox value={'video'}>{t('video', 'Videos')}</Checkbox>
                                                    <Checkbox value={'audio'}>{t('audio', 'Audio')}</Checkbox>
                                                    <Checkbox value={'document'}>{t('document', 'Documents')}</Checkbox>
                                                </Checkbox.Group>
                                            </FormItem>
                                            
                                            <FormItem name='destination' label={<>{t('ai-additional-destination-optional', 'Additional Destination (optional)')} <HelpPopover code={'custom-meta-ai-additional-destination'}/></>}>
                                                <Select name={'destination'}>
                                                    <Select.Option value={'none'}>{t('none', 'None')}</Select.Option>
                                                    <Select.Option value={'keywords'}>{t('keywords', 'Keywords')}</Select.Option>
                                                    <Select.Option value={'auto_tags'}>{t('auto-tags', 'Auto Tags')}</Select.Option>
                                                    <Select.Option value={'append_to_description'}>{t('append-to-description', 'Append to Description')}</Select.Option>
                                                    <Select.Option value={'ocr_content'}>{t('ocr-content', 'OCR Content')}</Select.Option>
                                                    <Select.Option value={'alt_text'}>{t('alt-text', 'Alt Text')}</Select.Option>
                                                </Select>
                                            </FormItem>
                                            
                                            <VerticalSpace size={'small'}>
                                                <Checkbox name={'auto_run'}>{t('auto-run', 'Auto-run on new uploads')} <HelpPopover code={'custom-meta-ai-auto-run'}/></Checkbox>
                                                
                                                {!values.free && (
                                                    <Checkbox name={'enable_controlled_vocab_creation'}>{t('enable-vocab-creation', 'Allow creating new values')} <HelpPopover code={'custom-meta-ai-enable-controlled-vocab-creation'}/></Checkbox>
                                                )}
                                            </VerticalSpace>
                                        </>
                                    )}
                                </>
                            )}

                            <Divider>{t('value-settings', 'Value Settings')}</Divider>

                            <FormItem name='multi'>
                                <Radio.Group name={'multi'}>
                                    <Radio value={false} style={{display:'block'}}>{t('single-value','Single-Value')}</Radio>
                                    <Radio value={true}>{t('multi-value','Multi-Value')?.toProperCase()}</Radio>
                                </Radio.Group>
                            </FormItem>


                            <FormItem name='free'>
                                <Checkbox name={'free'}>{t('free-text-input','Free text input?')} <HelpPopover code={'custom-meta-field-free'}/></Checkbox>
                            </FormItem>

                            <FormItem name='enable_rename'>
                                <Checkbox name={'enable_rename'}>{t('enable-rename','Available for file renaming?')} <HelpPopover code={'custom-meta-enable-rename'}/></Checkbox>
                            </FormItem>

                            {values.free && (
                                <FormItem name='value_type'>
                                    <label>{t('value-type','Value Type')}: <HelpPopover code={'custom-meta-field-value-type'}/></label>
                                    <br/>
                                    <Radio.Group name={'value_type'} disabled={cmf.id}>
                                        <Radio value={'text'} style={{display:'block'}}>{t('text','Text')}</Radio>
                                        <Radio value={'date'} style={{display:'block'}}>{t('date','Date')}</Radio>
                                        <Radio value={'datetime'} style={{display:'block'}}>{t('datetime','Date and Time')}</Radio>
                                    </Radio.Group>
                                </FormItem>
                            )}

                            {!values.free && (
                                <>
                                    <Divider>{t('controlled-vocabulary','Controlled Vocabulary')} <HelpPopover code={'custom-meta-values'}/></Divider>

                                    {!values.multi && (
                                        <FormItem name='enable_action_menu'>
                                            <Checkbox name={'enable_action_menu'}>{t('enable-action-menu','Show in Action Menu?')} <HelpPopover code={'custom-meta-enable-action-menu'}/></Checkbox>
                                        </FormItem>
                                    )}

                                    <FieldArray
                                        name='custom_meta_values_attributes'
                                        render={arrayHelpers => {
                                            const addNewValue = () => {
                                                arrayHelpers.push({text: values.new_value});
                                                setFieldValue('new_value', null)
                                            }

                                            return (
                                                <>
                                                    <div style={{display:'flex'}}>
                                                        <Form.Item name={'new_value'} style={{flex:'auto', marginRight:'.5em'}}>
                                                            <FormInput name={'new_value'} placeholder={t('placeholder-enter-new-value...','Enter New Value...')} onKeyDown={e => { if(e.keyCode == 13) addNewValue() }} autoComplete={'off'}/>
                                                        </Form.Item>

                                                        <Button onClick={addNewValue} style={{flex:'none'}}><PlusOutlined/> {t('add','Add')}</Button>
                                                    </div>

                                                    <List
                                                        dataSource={values.custom_meta_values_attributes}
                                                        itemLayout={'horizontal'}
                                                        locale={{emptyText:t('no-values-yet','No values yet.')}}
                                                        size={'small'}
                                                        renderItem={cmv => <CustomMetaValue cmv={cmv}/>}
                                                    />
                                                </>
                                            );
                                        }}
                                    />
                                </>
                            )}
                        </Form>
                    </Modal>)
                );
            }}
        </Formik>)
    );
}

const CustomMetaValue = ({cmv})=>{
    const {t} = useTranslation();
    const { values, setFieldValue } = useFormikContext();

    if(!values.custom_meta_values_attributes) return <></>

    const i = values.custom_meta_values_attributes?.indexOf(cmv)
    const toDestroy = cmv._destroy

    return (
        <List.Item
            style={{display: cmv._destroy ? 'red' : 'auto'}}
            actions={[
                <Button
                    onClick={() => {
                        if (cmv.id) {
                            setFieldValue(`custom_meta_values_attributes[${i}]._destroy`, !toDestroy)
                        } else {
                            arrayHelpers.remove(i)
                        }
                    }}
                >
                    {toDestroy ? <RollbackOutlined/> : <DeleteIcon/>}
                </Button>
            ]}
        >
            <Flex align={'center'} gap={6} justify={'flex-start'}>
                <ColorPicker
                    disabledAlpha
                    style={{marginLeft:'auto'}}
                    value={values.custom_meta_values_attributes[i]?.color_label}
                    allowClear
                    onChange={(c, hex) => {
                        setFieldValue(`custom_meta_values_attributes[${i}].color_label`, hex);
                    }}
                />

                <Typography.Text
                    disabled={toDestroy}
                    editable={!toDestroy && {
                        onChange: val => {
                            setFieldValue(`custom_meta_values_attributes[${i}].text`, val)
                        }
                    }}
                >
                    {toDestroy && t('removing','Removing') + ': '}
                    {cmv.text}
                </Typography.Text>
            </Flex>
        </List.Item>
    );
}

const ImportModal = ({visible, setVisible, onSuccess, ownerType, typeTitle}) => {
    const {t} = useTranslation();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [importing, setImporting] = useState(false);

    const handleImport = () => {
        if (!selectedFile) {
            message.error(t('message-no-file-selected', 'Please select a file to import.'));
            return;
        }

        setImporting(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        api.post('/api/custom_meta_fields/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
            const data = res.data;
            if (Array.isArray(data)) {
                message.success(t('message-custom-fields-imported', 'Imported {{count}} custom field(s).', { count: data.length }));
                data.forEach(cmf => onSuccess(cmf));
            } else {
                message.success(t('message-custom-field-imported', 'Custom field "{{name}}" imported.', { name: data.name }));
                onSuccess(data);
            }
            setSelectedFile(null);
            setImporting(false);
            setVisible(false);
        }).catch(err => {
            setImporting(false);
            message.error(err.data?.error || t('message-import-failed', 'Failed to import custom field.'));
        });
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setVisible(false);
    };

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            title={t('import-custom-field', `Import Custom ${typeTitle} Field`)}
            zIndex={9999}
            footer={
                <Space>
                    <Button
                        type="primary"
                        onClick={handleImport}
                        loading={importing}
                        disabled={!selectedFile}
                        icon={<ImportOutlined/>}
                    >
                        {t('import', 'Import')}
                    </Button>
                    <Button onClick={handleClose}>
                        {t('button-cancel', 'Cancel')}
                    </Button>
                </Space>
            }
        >
            <VerticalSpace>
                <Typography.Text type="secondary">
                    {t('import-instructions', 'Select a JSON file exported from another custom field to import its settings.')}
                </Typography.Text>

                <input
                    type="file"
                    ref={fileInputRef}
                    accept=".json"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                />
                <Button
                    icon={<UploadOutlined/>}
                    onClick={() => fileInputRef.current.click()}
                    style={{ width: '100%' }}
                >
                    {selectedFile ? selectedFile.name : t('select-json-file', 'Select JSON File...')}
                </Button>
            </VerticalSpace>
        </Modal>
    );
}
